import { IntakeDiffRow, IntakeExtractedFields } from '../interfaces/intake';

export interface IntakeDiffView {
  field: string;
  label: string;
  /** Stored value as D1 sent it in `old` (dates Y-m-d); null renders as the empty marker. */
  now: string | null;
  /** Value in the mail as D1 sent it in `new`; null renders as the empty marker. */
  next: string | null;
  /** True when both present sides are ISO dates and go through the date pipe. */
  date: boolean;
}

/** D1 IntakeMailResource::DIFF_FIELDS, in its order. */
const LABELS: Record<string, string> = {
  title: 'intake.panel.f_title',
  deadline: 'intake.panel.f_deadline',
  start_date: 'intake.panel.f_start_date',
  tariff: 'intake.panel.f_tariff',
  hours_per_week: 'intake.panel.f_hours',
  location: 'intake.panel.f_location',
  description: 'intake.update.f_description',
};
const DATE_FIELDS = ['deadline', 'start_date'];
const ISO_DAY = /^(\d{4}-\d{2}-\d{2})(?:[T ][\d:.]+(?:Z|[+-]\d{2}:?\d{2})?)?$/;

function text(v: unknown): string | null {
  if (v === null || v === undefined) { return null; }
  const s = String(v).trim();
  return s === '' ? null : s;
}

function isDay(v: string | null): boolean {
  const day = v === null ? null : ISO_DAY.exec(v)?.[1];
  return !!day && !isNaN(Date.parse(day));
}

/**
 * D1's update_diff as sent (IntakeDedup::same() decides what differs; nothing is re-filtered here),
 * labelled and with a flag for the date pipe. Unknown fields have no label and are dropped.
 */
export function intakeDiffRows(diff: IntakeDiffRow[] | null | undefined): IntakeDiffView[] {
  return (Array.isArray(diff) ? diff : []).flatMap(row => {
    const label = LABELS[row?.field];
    if (!label) { return []; }
    const now = text(row.old);
    const next = text(row.new);
    const date = DATE_FIELDS.includes(row.field) && (now === null || isDay(now)) && (next === null || isDay(next));
    return [{ field: row.field, label, now, next, date }];
  });
}

/** Labels of the compared fields the mail carries that are not shown as changed. */
export function intakeUnchangedLabels(fields: IntakeExtractedFields, shown: IntakeDiffView[]): string[] {
  const changed = new Set(shown.map(r => r.field));
  return Object.keys(LABELS)
    .filter(key => !changed.has(key) && text((fields as any)?.[key]) !== null)
    .map(key => LABELS[key]);
}

/** Parser keys (IntakeFieldValidator::MAP) and stored keys, to their form label. */
const FIELD_LABELS: Record<string, string> = {
  title: 'intake.panel.f_title',
  buyer: 'intake.panel.f_company',
  company: 'intake.panel.f_company',
  reference: 'intake.panel.f_reference',
  deadline: 'intake.panel.f_deadline',
  start_date: 'intake.panel.f_start_date',
  end_date: 'intake.panel.f_end_date',
  hours_per_week: 'intake.panel.f_hours',
  rate: 'intake.panel.f_tariff',
  tariff: 'intake.panel.f_tariff',
  location: 'intake.panel.f_location',
  portal_link: 'intake.panel.f_url',
  url: 'intake.panel.f_url',
  description: 'intake.update.f_description',
};

/** Translation key for a field name in a note; an unknown name falls back to the generic word. */
export function intakeFieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? 'intake.panel.f_unknown';
}
